/**
 * DashboardEditorPage — compose and manage widgets in a dashboard.
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiFetch } from "../../lib/config";

interface WidgetItem {
  id: string;
  widget_type: string;
  position: number;
  title: string;
  config: Record<string, unknown>;
}

interface DashboardDetail {
  id: string;
  name: string;
  slug: string;
  is_public: boolean;
  share_token: string;
  widgets: WidgetItem[];
}

type WidgetType = "status_summary" | "metric_chart" | "incident_list" | "on_call_status" | "slo_burn";

const WIDGET_TYPES: { value: WidgetType; label: string }[] = [
  { value: "status_summary", label: "Status Summary" },
  { value: "metric_chart", label: "Metric Chart" },
  { value: "incident_list", label: "Incident List" },
  { value: "on_call_status", label: "On-Call Status" },
  { value: "slo_burn", label: "SLO Burn Rate" },
];

async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await apiFetch(url, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json() as Promise<T>;
}

export function DashboardEditorPage() {
  const { id } = useParams<{ id: string }>();
  const [dashboard, setDashboard] = useState<DashboardDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add widget form
  const [showAdd, setShowAdd] = useState(false);
  const [addType, setAddType] = useState<WidgetType>("status_summary");
  const [addTitle, setAddTitle] = useState("");
  const [addConfig, setAddConfig] = useState<Record<string, string>>({});
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const load = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const d = await fetchJSON<DashboardDetail>(`/api/v1/dashboards/${id}/`);
      setDashboard(d);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const buildConfig = (): Record<string, unknown> => {
    if (addType === "metric_chart") {
      return { service: addConfig.service ?? "", metric: addConfig.metric ?? "", range: addConfig.range ?? "1h" };
    }
    if (addType === "on_call_status") return { team_id: addConfig.team_id ?? "" };
    if (addType === "slo_burn") return { slo_id: addConfig.slo_id ?? "" };
    if (addType === "incident_list") return { limit: parseInt(addConfig.limit ?? "5", 10) };
    return {};
  };

  const handleAddWidget = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dashboard) return;
    setAddError(null);
    setAdding(true);
    try {
      const nextPos = dashboard.widgets.length > 0
        ? Math.max(...dashboard.widgets.map((w) => w.position)) + 1
        : 0;
      await fetchJSON(`/api/v1/dashboards/${dashboard.id}/widgets/`, {
        method: "POST",
        body: JSON.stringify({
          widget_type: addType,
          position: nextPos,
          title: addTitle,
          config: buildConfig(),
        }),
      });
      setShowAdd(false);
      setAddTitle("");
      setAddConfig({});
      await load();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : "Add failed");
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveWidget = async (widgetId: string) => {
    if (!dashboard) return;
    await apiFetch(`/api/v1/dashboards/${dashboard.id}/widgets/${widgetId}/`, { method: "DELETE" });
    await load();
  };

  const handleMove = async (widgetId: string, direction: "up" | "down") => {
    if (!dashboard) return;
    const sorted = [...dashboard.widgets].sort((a, b) => a.position - b.position);
    const idx = sorted.findIndex((w) => w.id === widgetId);
    if (direction === "up" && idx === 0) return;
    if (direction === "down" && idx === sorted.length - 1) return;

    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    const newOrder = sorted.map((w, i) => {
      if (i === idx) return { id: w.id, position: sorted[swapIdx].position };
      if (i === swapIdx) return { id: w.id, position: sorted[idx].position };
      return { id: w.id, position: w.position };
    });

    await fetchJSON(`/api/v1/dashboards/${dashboard.id}/widgets/reorder/`, {
      method: "POST",
      body: JSON.stringify(newOrder),
    });
    await load();
  };

  const copyShareLink = () => {
    if (!dashboard) return;
    const url = `${window.location.origin}/dashboard/${dashboard.share_token}`;
    navigator.clipboard.writeText(url).catch(() => {});
  };

  if (loading) return <section className="aptean-page"><p style={{ color: "var(--aptean-text-secondary)" }}>Loading...</p></section>;
  if (error) return <section className="aptean-page"><p role="alert" style={{ color: "var(--aptean-error, #e74c3c)" }}>{error}</p></section>;
  if (!dashboard) return null;

  const sorted = [...dashboard.widgets].sort((a, b) => a.position - b.position);

  return (
    <section className="aptean-page">
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, color: "var(--aptean-text-primary)" }}>{dashboard.name}</h2>
        <div style={{ display: "flex", gap: 8 }}>
          {dashboard.is_public && (
            <button className="aptean-btn" onClick={copyShareLink}>
              Share Link
            </button>
          )}
          <button
            className="aptean-btn aptean-btn--primary"
            onClick={() => setShowAdd((v) => !v)}
          >
            {showAdd ? "Cancel" : "+ Add Widget"}
          </button>
        </div>
      </header>

      {showAdd && (
        <form
          onSubmit={handleAddWidget}
          className="aptean-card"
          style={{ marginTop: 16, padding: 16 }}
          aria-label="Add Widget"
        >
          <h3 style={{ marginTop: 0 }}>Add Widget</h3>
          {addError && (
            <p role="alert" style={{ color: "var(--aptean-error, #e74c3c)" }}>
              {addError}
            </p>
          )}
          <div style={{ display: "grid", gap: 10 }}>
            <label>
              Widget Type
              <select
                className="aptean-input"
                value={addType}
                onChange={(e) => {
                  setAddType(e.target.value as WidgetType);
                  setAddConfig({});
                }}
              >
                {WIDGET_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Title (optional)
              <input
                className="aptean-input"
                value={addTitle}
                onChange={(e) => setAddTitle(e.target.value)}
                placeholder="Widget title"
              />
            </label>

            {/* Type-specific config */}
            {addType === "metric_chart" && (
              <>
                <label>
                  Service
                  <input
                    className="aptean-input"
                    value={addConfig.service ?? ""}
                    onChange={(e) => setAddConfig({ ...addConfig, service: e.target.value })}
                    placeholder="e.g. erp"
                  />
                </label>
                <label>
                  Metric name
                  <input
                    className="aptean-input"
                    value={addConfig.metric ?? ""}
                    onChange={(e) => setAddConfig({ ...addConfig, metric: e.target.value })}
                    placeholder="e.g. cpu_percent"
                    data-testid="metric-config-field"
                  />
                </label>
                <label>
                  Range
                  <select
                    className="aptean-input"
                    value={addConfig.range ?? "1h"}
                    onChange={(e) => setAddConfig({ ...addConfig, range: e.target.value })}
                  >
                    <option value="5m">5 minutes</option>
                    <option value="1h">1 hour</option>
                    <option value="1d">1 day</option>
                  </select>
                </label>
              </>
            )}
            {addType === "on_call_status" && (
              <label>
                Team ID
                <input
                  className="aptean-input"
                  value={addConfig.team_id ?? ""}
                  onChange={(e) => setAddConfig({ ...addConfig, team_id: e.target.value })}
                  placeholder="Team UUID"
                />
              </label>
            )}
            {addType === "slo_burn" && (
              <label>
                SLO ID
                <input
                  className="aptean-input"
                  value={addConfig.slo_id ?? ""}
                  onChange={(e) => setAddConfig({ ...addConfig, slo_id: e.target.value })}
                  placeholder="SLO UUID"
                />
              </label>
            )}
            {addType === "incident_list" && (
              <label>
                Limit
                <input
                  className="aptean-input"
                  type="number"
                  min="1"
                  max="20"
                  value={addConfig.limit ?? "5"}
                  onChange={(e) => setAddConfig({ ...addConfig, limit: e.target.value })}
                />
              </label>
            )}
          </div>
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <button type="submit" className="aptean-btn aptean-btn--primary" disabled={adding}>
              {adding ? "Adding..." : "Add"}
            </button>
            <button type="button" className="aptean-btn" onClick={() => setShowAdd(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {sorted.length === 0 ? (
        <p style={{ marginTop: 24, color: "var(--aptean-text-secondary)" }}>
          No widgets yet. Add one above.
        </p>
      ) : (
        <ul className="aptean-widget-list" style={{ marginTop: 16, listStyle: "none", padding: 0, display: "grid", gap: 10 }}>
          {sorted.map((w, idx) => (
            <li
              key={w.id}
              className="aptean-card"
              style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}
            >
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontWeight: 600, color: "var(--aptean-text-primary)" }}>
                  {w.title || WIDGET_TYPES.find((t) => t.value === w.widget_type)?.label}
                </p>
                <p style={{ margin: "4px 0 0", fontSize: "0.8rem", color: "var(--aptean-text-secondary)" }}>
                  {w.widget_type}
                </p>
              </div>
              <button
                className="aptean-btn"
                style={{ padding: "4px 8px" }}
                disabled={idx === 0}
                onClick={() => handleMove(w.id, "up")}
                aria-label="Move up"
              >
                &#9650;
              </button>
              <button
                className="aptean-btn"
                style={{ padding: "4px 8px" }}
                disabled={idx === sorted.length - 1}
                onClick={() => handleMove(w.id, "down")}
                aria-label="Move down"
              >
                &#9660;
              </button>
              <button
                className="aptean-btn aptean-btn--danger"
                style={{ padding: "4px 8px", fontSize: "0.8rem" }}
                onClick={() => handleRemoveWidget(w.id)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
